import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { ApplicationName } from '@makemydeal/dr-shared-types';

export const FIRST_PENCIL_EVENTS = {
    dealLaunched: 'MV:FirstPencil:DealLaunched',
    autoApplyTemplateBypass: 'MV:FirstPencil:AutoApplyTemplateBypass',
    dealStructureChangedPostCreate: 'MV:FirstPencil:DealStructureChangedPostCreate',
    createClicked: 'MV:FirstPencil:CreateClicked',
    rrDisplayed: 'MV:FirstPencil:RrDisplayed',
    programSelected: 'MV:FirstPencil:ProgramSelected'
} as const;

/** The snake_case identifiers from F280192-Measurement-Plan.md, so NRQL can filter on that vocabulary directly. */
const METRIC_NAMES = {
    dealLaunched: 'mv_deal_launched',
    autoApplyTemplateBypass: 'auto_apply_template_bypass',
    dealStructureChangedPostCreate: 'deal_structure_changed_post_create',
    createClicked: 'first_pencil_create_clicked',
    rrDisplayed: 'first_pencil_rr_displayed',
    programSelected: 'first_pencil_program_selected'
} as const;

/** dealXgId, dealXgVersion, mmdDealerId and correlationId are global NR custom attributes set at deal init, so they ride along automatically and don't need to be repeated here. */
const baseAttributes = () => ({ appName: ApplicationName.CMD });

export type FirstPencilSetupModeAttr = 'template' | 'programs';

export type AutoApplyBypassReason = 'templateApplied' | 'autoSelectProgramSaved';

export const logFirstPencilDealLaunched = (): void => {
    logNewRelicPageAction(FIRST_PENCIL_EVENTS.dealLaunched, {
        ...baseAttributes(),
        metricName: METRIC_NAMES.dealLaunched
    });
};

export const logFirstPencilAutoApplyTemplateBypass = (bypassReason: AutoApplyBypassReason): void => {
    logNewRelicPageAction(FIRST_PENCIL_EVENTS.autoApplyTemplateBypass, {
        ...baseAttributes(),
        metricName: METRIC_NAMES.autoApplyTemplateBypass,
        bypassReason
    });
};

export const logFirstPencilDealStructureChangedPostCreate = (triggerActionType: string | undefined): void => {
    logNewRelicPageAction(FIRST_PENCIL_EVENTS.dealStructureChangedPostCreate, {
        ...baseAttributes(),
        metricName: METRIC_NAMES.dealStructureChangedPostCreate,
        triggerActionType: triggerActionType ?? ''
    });
};

export type FirstPencilCreateClickedEventData = {
    setupMode: FirstPencilSetupModeAttr | undefined;
    elapsedMs: number;
    scenarioCount: number;
};

export const logFirstPencilCreateClicked = ({ setupMode, elapsedMs, scenarioCount }: FirstPencilCreateClickedEventData): void => {
    logNewRelicPageAction(FIRST_PENCIL_EVENTS.createClicked, {
        ...baseAttributes(),
        metricName: METRIC_NAMES.createClicked,
        setupMode: setupMode ?? '',
        elapsedMs,
        scenarioCount
    });
};

/** dealTypesReturned is joined to a string — New Relic custom attributes are not array-typed. */
export const logFirstPencilRrDisplayed = (offerType: string, dealTypesReturned: string[]): void => {
    logNewRelicPageAction(FIRST_PENCIL_EVENTS.rrDisplayed, {
        ...baseAttributes(),
        metricName: METRIC_NAMES.rrDisplayed,
        offerType,
        dealTypesReturned: dealTypesReturned.join(',')
    });
};

export const logFirstPencilProgramSelected = (offerType: string, lenderLegalName: string | undefined): void => {
    logNewRelicPageAction(FIRST_PENCIL_EVENTS.programSelected, {
        ...baseAttributes(),
        metricName: METRIC_NAMES.programSelected,
        offerType,
        lenderLegalName: lenderLegalName ?? ''
    });
};
